import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchProjects, createProject, updateProject, setBaseline,
  fetchTasks, createTask, updateTask, updateTasksBulk, deleteTask,
  fetchDeps, addDep, removeDep, fetchMembers, currentMemberId,
  criticalPath, cascade, toDate, toISO, addDays, daysBetween, durationDays, D,
  PROJECT_STATUSES, PROJECT_COLOURS,
  type Project, type Task, type Dep, type Member,
} from '../lib/projects';
import { onFail } from '../lib/errors';
import './projects.css';

/* =====================================================================================
   Projects: plans you can drag. The left half is the work-breakdown table; the right is
   the Gantt, one SVG with no library behind it, where a bar can be moved or resized a
   day at a time, a milestone is a diamond, the baseline shows as a ghost under each bar,
   dependencies draw their elbows, and the critical path can be lit up. Cascade, on by
   default, is the behaviour people pay other tools for: slip a task and everything
   downstream that would now be impossible moves with it.
   ===================================================================================== */

const ROW = 34;
const ZOOMS: Record<string, number> = { day: 28, week: 10, month: 3.6 };
const fmtD = (s: string | null | undefined) => (s ? toDate(s)
  .toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '');
const STATUS_PILL: Record<string, string> = {
  planning: 'mute', active: 'info', on_hold: 'warn', done: 'ok', archived: 'mute',
};

export function ProjectsView() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchProjects().then(setProjects).catch(onFail('Projects', () => setProjects([])));
    fetchMembers().then(setMembers).catch(() => setMembers([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = projects.find((p) => p.id === openId) ?? null;
  const memberName = (id: string | null) => members.find((m) => m.id === id)?.full_name ?? '';

  if (open) {
    return <PlanView project={open} members={members} memberName={memberName}
                     onBack={() => { setOpenId(null); load(); }}
                     onProjectChanged={load} />;
  }

  return (
    <div className="page projects">
      <div className="page-head">
        <h1 className="page-title">Projects</h1>
        <p className="page-sub">
          Plans with real scheduling: phases and tasks, owners, dependencies with lag,
          milestones, baselines and the critical path, on a Gantt you drag.
        </p>
      </div>
      {msg && <p className="block-note" role="status">{msg}</p>}
      <div className="toolbar">
        <button className="btn primary" onClick={() => setCreating(true)}>New project</button>
      </div>
      <section className="pj-grid">
        {projects.length === 0 && (
          <p className="module-empty">No projects yet. The first plan is one button away.</p>
        )}
        {projects.map((p) => (
          <ProjectCard key={p.id} p={p} ownerName={memberName(p.owner_id)}
                       onOpen={() => setOpenId(p.id)} />
        ))}
      </section>
      {creating && (
        <NewProject members={members} onClose={() => setCreating(false)}
          onDone={(n) => { setCreating(false); setMsg(`${n} created. Open it and lay out the plan.`); load(); }} />
      )}
    </div>
  );
}

function ProjectCard({ p, ownerName, onOpen }: { p: Project; ownerName: string; onOpen: () => void }) {
  const [stats, setStats] = useState<{ n: number; pct: number; from: string | null; to: string | null } | null>(null);
  useEffect(() => {
    fetchTasks(p.id).then((ts) => {
      const parents = new Set(ts.filter((t) => t.parent_id).map((t) => t.parent_id));
      const leaves = ts.filter((t) => !parents.has(t.id));
      const wsum = leaves.reduce((s, t) => s + durationDays(t), 0);
      const pct = wsum ? Math.round(leaves.reduce((s, t) => s + t.percent_complete * durationDays(t), 0) / wsum) : 0;
      setStats({
        n: leaves.length, pct,
        from: ts.length ? ts.reduce((a, t) => (t.starts_on < a ? t.starts_on : a), ts[0].starts_on) : null,
        to: ts.length ? ts.reduce((a, t) => (t.ends_on > a ? t.ends_on : a), ts[0].ends_on) : null,
      });
    }).catch(() => setStats(null));
  }, [p.id]);
  return (
    <article className="card pj-card" onClick={onOpen}>
      <div className="pj-card-head">
        <span className="pj-dot" style={{ background: p.colour }} />
        <strong>{p.name}</strong>
        <span className={`pill ${STATUS_PILL[p.status] ?? 'mute'}`}>{p.status.replace('_', ' ')}</span>
      </div>
      <div className="cell-sub">
        {p.number}{ownerName && <> · {ownerName}</>}
        {stats?.from && <> · {fmtD(stats.from)} to {fmtD(stats.to)}</>}
      </div>
      {stats && stats.n > 0 && (
        <div className="pj-progress">
          <div className="pj-progress-fill" style={{ width: `${stats.pct}%`, background: p.colour }} />
          <span className="pj-progress-label">{stats.pct}% · {stats.n} task{stats.n === 1 ? '' : 's'}</span>
        </div>
      )}
    </article>
  );
}

function NewProject({ members, onClose, onDone }: {
  members: Member[]; onClose: () => void; onDone: (n: string) => void;
}) {
  const [f, setF] = useState({ name: '', ownerId: '', colour: PROJECT_COLOURS[0], description: '' });
  const [err, setErr] = useState<string | null>(null);
  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer pj-drawer">
        <h2>New project</h2>
        {err && <p className="block-note">{err}</p>}
        <label className="cf-field"><span>Name</span>
          <input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
        <label className="cf-field"><span>Owner</span>
          <select className="input sel" value={f.ownerId} onChange={(e) => setF({ ...f, ownerId: e.target.value })}>
            <option value="">Unowned</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
          </select></label>
        <label className="cf-field"><span>Colour</span>
          <span className="pj-swatches">
            {PROJECT_COLOURS.map((c) => (
              <button key={c} className={`pj-swatch ${f.colour === c ? 'on' : ''}`}
                      style={{ background: c }} onClick={() => setF({ ...f, colour: c })} />
            ))}
          </span></label>
        <label className="cf-field"><span>Description</span>
          <input className="input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></label>
        <div className="exp-decide-actions">
          <button className="btn primary" onClick={async () => {
            try {
              if (!f.name.trim()) throw new Error('A name first.');
              await createProject({ name: f.name.trim(), ownerId: f.ownerId || undefined,
                colour: f.colour, description: f.description || undefined });
              onDone(f.name.trim());
            } catch (e) { setErr(String((e as Error).message)); }
          }}>Create</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}

/* ==================================================================== the plan */
interface Row { task: Task; depth: number; isParent: boolean }

function PlanView({ project, members, memberName, onBack, onProjectChanged }: {
  project: Project; members: Member[]; memberName: (id: string | null) => string;
  onBack: () => void; onProjectChanged: () => void;
}) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [deps, setDeps] = useState<Dep[]>([]);
  const [zoom, setZoom] = useState<'day' | 'week' | 'month'>('day');
  const [cascadeOn, setCascadeOn] = useState(true);
  const [showCritical, setShowCritical] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const [me, setMe] = useState<string | null>(null);
  const [openTask, setOpenTask] = useState<Task | null>(null);
  const [addingUnder, setAddingUnder] = useState<string | null | 'top'>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [drag, setDrag] = useState<{ id: string; mode: 'move' | 'resize'; x0: number; delta: number } | null>(null);

  const load = useCallback(() => {
    fetchTasks(project.id).then(setTasks).catch(onFail('The plan', () => setTasks([])));
    fetchDeps(project.id).then(setDeps).catch(() => setDeps([]));
  }, [project.id]);
  useEffect(() => { load(); currentMemberId().then(setMe); }, [load]);

  const rows: Row[] = useMemo(() => {
    const parents = new Set(tasks.filter((t) => t.parent_id).map((t) => t.parent_id as string));
    const tops = tasks.filter((t) => !t.parent_id);
    const out: Row[] = [];
    for (const t of tops) {
      const isParent = parents.has(t.id);
      if (mineOnly && !isParent && t.assignee_id !== me) continue;
      out.push({ task: t, depth: 0, isParent });
      for (const c of tasks.filter((x) => x.parent_id === t.id)) {
        if (mineOnly && c.assignee_id !== me) continue;
        out.push({ task: c, depth: 1, isParent: false });
      }
    }
    return out;
  }, [tasks, mineOnly, me]);

  const { critical } = useMemo(() => criticalPath(tasks, deps), [tasks, deps]);

  // timeline range: a week of air before, a fortnight after
  const [rangeFrom, rangeTo] = useMemo(() => {
    if (!tasks.length) {
      const today = toISO(new Date());
      return [addDays(today, -7), addDays(today, 35)];
    }
    const from = tasks.reduce((a, t) => (t.starts_on < a ? t.starts_on : a), tasks[0].starts_on);
    const to = tasks.reduce((a, t) => (t.ends_on > a ? t.ends_on : a), tasks[0].ends_on);
    return [addDays(from, -7), addDays(to, 14)];
  }, [tasks]);
  const dayW = ZOOMS[zoom];
  const totalDays = daysBetween(rangeFrom, rangeTo) + 1;
  const X = (iso: string) => daysBetween(rangeFrom, iso) * dayW;
  const chartW = totalDays * dayW;
  const chartH = rows.length * ROW;

  // the effective dates of a bar mid-drag
  const dragged = (t: Task): { s: string; e: string } => {
    if (!drag || drag.id !== t.id) return { s: t.starts_on, e: t.ends_on };
    if (drag.mode === 'move') return { s: addDays(t.starts_on, drag.delta), e: addDays(t.ends_on, drag.delta) };
    const e = addDays(t.ends_on, drag.delta);
    return { s: t.starts_on, e: e < t.starts_on ? t.starts_on : e };
  };

  async function commitDrag() {
    if (!drag || drag.delta === 0) { setDrag(null); return; }
    const t = tasks.find((x) => x.id === drag.id);
    setDrag(null);
    if (!t) return;
    const { s, e } = {
      s: drag.mode === 'move' ? addDays(t.starts_on, drag.delta) : t.starts_on,
      e: (() => { const v = addDays(t.ends_on, drag.delta); return v < t.starts_on && drag.mode === 'resize' ? t.starts_on : v; })(),
    };
    try {
      const moves = cascadeOn ? cascade(tasks, deps, t.id, s, e)
        : [{ id: t.id, starts_on: s, ends_on: e }];
      await updateTasksBulk(moves);
      if (moves.length > 1) setMsg(`${moves.length - 1} downstream task${moves.length === 2 ? '' : 's'} moved with it.`);
      load();
    } catch (err) { setMsg(String((err as Error).message)); load(); }
  }

  // month labels + weekend bands
  const monthMarks = useMemo(() => {
    const out: { x: number; label: string }[] = [];
    const d = toDate(rangeFrom);
    d.setDate(1);
    while (toISO(d) <= rangeTo) {
      if (toISO(d) >= rangeFrom) {
        out.push({ x: X(toISO(d)), label: d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }) });
      } else {
        out.push({ x: 0, label: d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }) });
      }
      d.setMonth(d.getMonth() + 1);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeFrom, rangeTo, dayW]);

  const today = toISO(new Date());

  return (
    <div className="page projects">
      <div className="pj-planhead">
        <div className="back-link" onClick={onBack}>All projects</div>
        <h1 className="page-title">
          <span className="pj-dot" style={{ background: project.colour }} /> {project.name}
        </h1>
        <div className="pj-tools">
          <select className="input sel pj-status" value={project.status}
                  onChange={(e) => updateProject(project.id, { status: e.target.value } as Partial<Project>)
                    .then(onProjectChanged).catch((x) => setMsg(x.message))}>
            {PROJECT_STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select className="input sel" value={zoom} onChange={(e) => setZoom(e.target.value as 'day')}>
            <option value="day">Day</option><option value="week">Week</option><option value="month">Month</option>
          </select>
          <label className="lv-check"><input type="checkbox" checked={cascadeOn}
            onChange={(e) => setCascadeOn(e.target.checked)} /><span>Cascade delays</span></label>
          <label className="lv-check"><input type="checkbox" checked={showCritical}
            onChange={(e) => setShowCritical(e.target.checked)} /><span>Critical path</span></label>
          <label className="lv-check"><input type="checkbox" checked={mineOnly}
            onChange={(e) => setMineOnly(e.target.checked)} /><span>My tasks</span></label>
          <button className="btn" onClick={() =>
            setBaseline(project.id).then((n) => { setMsg(`Baseline set across ${n} tasks.`); load(); onProjectChanged(); })
              .catch((x) => setMsg(x.message))}>Set baseline</button>
          <button className="btn primary" onClick={() => setAddingUnder('top')}>Add task</button>
        </div>
      </div>
      {msg && <p className="block-note" role="status">{msg}</p>}
      {project.baseline_set_at && (
        <p className="muted pj-bnote">Baseline set {new Date(project.baseline_set_at).toLocaleDateString('en-GB')}; ghost bars show it.</p>
      )}

      {rows.length === 0 ? (
        <section className="card"><p className="module-empty">
          An empty plan. Add the first task; phases are just tasks with sub-tasks under them.
        </p></section>
      ) : (
        <section className="card pj-plan" onPointerUp={commitDrag} onPointerLeave={() => drag && commitDrag()}>
          <div className="pj-split">
            <div className="pj-table">
              <div className="pj-thead">
                <span>Task</span><span>Who</span><span>Start</span><span>End</span><span>%</span>
              </div>
              {rows.map(({ task: t, depth, isParent }) => (
                <div key={t.id}
                     className={`pj-trow ${isParent ? 'parent' : ''} ${t.assignee_id === me ? 'mine' : ''}`}
                     onClick={() => !isParent && setOpenTask(t)}>
                  <span className="pj-tname" style={{ paddingLeft: depth * 18 }}>
                    {t.is_milestone && <span className="pj-mini-diamond" />}
                    {t.name}
                    {!isParent && depth === 0 && (
                      <button className="pj-addsub" title="Add a sub-task"
                              onClick={(e) => { e.stopPropagation(); setAddingUnder(t.id); }}>+</button>
                    )}
                    {isParent && (
                      <button className="pj-addsub" title="Add a sub-task"
                              onClick={(e) => { e.stopPropagation(); setAddingUnder(t.id); }}>+</button>
                    )}
                  </span>
                  <span className="pj-twho">{memberName(t.assignee_id) || (isParent ? '' : 'unassigned')}</span>
                  <span>{fmtD(t.starts_on)}</span>
                  <span>{fmtD(t.ends_on)}</span>
                  <span>{isParent ? '' : `${t.percent_complete}`}</span>
                </div>
              ))}
            </div>
            <div className="pj-chartwrap">
              <div className="pj-months" style={{ width: chartW }}>
                {monthMarks.map((m, i) => (
                  <span key={i} className="pj-month" style={{ left: m.x }}>{m.label}</span>
                ))}
              </div>
              <svg className="pj-chart" width={chartW} height={chartH}
                   style={{ touchAction: 'none' }}
                   onPointerMove={(e) => {
                     if (!drag) return;
                     const delta = Math.round((e.clientX - drag.x0) / dayW);
                     if (delta !== drag.delta) setDrag({ ...drag, delta });
                   }}>
                {/* weekend + grid bands */}
                {zoom !== 'month' && Array.from({ length: totalDays }).map((_, i) => {
                  const d = new Date(toDate(rangeFrom).getTime() + i * D);
                  const wd = d.getDay();
                  return (wd === 0 || wd === 6)
                    ? <rect key={i} x={i * dayW} y={0} width={dayW} height={chartH} className="pj-weekend" />
                    : null;
                })}
                {monthMarks.map((m, i) => (
                  <line key={i} x1={m.x} x2={m.x} y1={0} y2={chartH} className="pj-gridline" />
                ))}
                {rows.map((_, i) => (
                  <line key={i} x1={0} x2={chartW} y1={(i + 1) * ROW} y2={(i + 1) * ROW} className="pj-rowline" />
                ))}
                {/* today */}
                {today >= rangeFrom && today <= rangeTo && (
                  <line x1={X(today) + dayW / 2} x2={X(today) + dayW / 2} y1={0} y2={chartH} className="pj-today" />
                )}
                {/* dependency elbows */}
                {deps.map((d) => {
                  const fromIdx = rows.findIndex((r) => r.task.id === d.depends_on_task_id);
                  const toIdx = rows.findIndex((r) => r.task.id === d.task_id);
                  if (fromIdx < 0 || toIdx < 0) return null;
                  const p = dragged(rows[fromIdx].task); const sTask = dragged(rows[toIdx].task);
                  const x1 = X(p.e) + dayW; const y1 = fromIdx * ROW + ROW / 2;
                  const x2 = X(sTask.s); const y2 = toIdx * ROW + ROW / 2;
                  const bend = Math.max(x1 + 8, x2 - 8);
                  return (
                    <g key={d.id} className="pj-dep">
                      <path d={`M ${x1} ${y1} H ${bend} V ${y2} H ${x2}`} />
                      <path d={`M ${x2} ${y2} l -6 -4 v 8 z`} className="pj-dep-head" />
                    </g>
                  );
                })}
                {/* bars */}
                {rows.map(({ task: t, isParent }, i) => {
                  const { s, e } = dragged(t);
                  const x = X(s); const w = Math.max((daysBetween(s, e) + 1) * dayW, dayW * 0.6);
                  const y = i * ROW;
                  const crit = showCritical && critical.has(t.id);
                  if (isParent) {
                    const kids = tasks.filter((k) => k.parent_id === t.id);
                    if (!kids.length) return null;
                    const ps = kids.reduce((a, k) => (dragged(k).s < a ? dragged(k).s : a), dragged(kids[0]).s);
                    const pe = kids.reduce((a, k) => (dragged(k).e > a ? dragged(k).e : a), dragged(kids[0]).e);
                    const px = X(ps); const pw = (daysBetween(ps, pe) + 1) * dayW;
                    return (
                      <g key={t.id}>
                        <rect x={px} y={y + 10} width={pw} height={7} rx={2} className="pj-summary" />
                        <rect x={px} y={y + 10} width={3} height={13} className="pj-summary" />
                        <rect x={px + pw - 3} y={y + 10} width={3} height={13} className="pj-summary" />
                      </g>
                    );
                  }
                  if (t.is_milestone) {
                    const cx = X(s) + dayW / 2; const cy = y + ROW / 2;
                    return (
                      <g key={t.id} className="pj-barwrap"
                         onPointerDown={(ev) => { ev.preventDefault(); setDrag({ id: t.id, mode: 'move', x0: ev.clientX, delta: 0 }); }}>
                        <path d={`M ${cx} ${cy - 9} l 9 9 l -9 9 l -9 -9 z`}
                              className={`pj-milestone ${t.percent_complete >= 100 ? 'done' : ''} ${crit ? 'crit' : ''}`} />
                      </g>
                    );
                  }
                  return (
                    <g key={t.id} className="pj-barwrap">
                      {t.baseline_starts_on && t.baseline_ends_on && (
                        <rect x={X(t.baseline_starts_on)} y={y + 24}
                              width={(daysBetween(t.baseline_starts_on, t.baseline_ends_on) + 1) * dayW}
                              height={4} rx={2} className="pj-ghost" />
                      )}
                      <rect x={x} y={y + 7} width={w} height={16} rx={5}
                            className={`pj-bar ${crit ? 'crit' : ''}`}
                            style={{ fill: project.colour, opacity: t.percent_complete >= 100 ? 0.45 : 0.85 }}
                            onPointerDown={(ev) => { ev.preventDefault(); setDrag({ id: t.id, mode: 'move', x0: ev.clientX, delta: 0 }); }} />
                      {t.percent_complete > 0 && (
                        <rect x={x} y={y + 7} width={w * Math.min(t.percent_complete, 100) / 100}
                              height={16} rx={5} className="pj-barfill" pointerEvents="none" />
                      )}
                      <rect x={x + w - 6} y={y + 7} width={8} height={16} className="pj-handle"
                            onPointerDown={(ev) => { ev.preventDefault(); ev.stopPropagation(); setDrag({ id: t.id, mode: 'resize', x0: ev.clientX, delta: 0 }); }} />
                      {zoom === 'day' && (
                        <text x={x + w + 6} y={y + 19} className="pj-barlabel" pointerEvents="none">
                          {memberName(t.assignee_id).split(' ')[0] ?? ''}
                        </text>
                      )}
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>
        </section>
      )}

      {addingUnder !== null && (
        <AddTask projectId={project.id} parentId={addingUnder === 'top' ? undefined : addingUnder}
          members={members}
          siblingMax={tasks.length ? tasks.reduce((a, t) => Math.max(a, t.sort_order), 0) + 1 : 0}
          onClose={() => setAddingUnder(null)}
          onDone={() => { setAddingUnder(null); load(); }} />
      )}
      {openTask && (
        <TaskDrawer task={openTask} tasks={tasks} deps={deps} members={members}
          onClose={() => setOpenTask(null)}
          onChanged={() => { setOpenTask(null); load(); }} onMsg={setMsg} />
      )}
    </div>
  );
}

function AddTask({ projectId, parentId, members, siblingMax, onClose, onDone }: {
  projectId: string; parentId?: string; members: Member[]; siblingMax: number;
  onClose: () => void; onDone: () => void;
}) {
  const today = toISO(new Date());
  const [f, setF] = useState({ name: '', startsOn: today, endsOn: addDays(today, 4),
    isMilestone: false, assigneeId: '' });
  const [err, setErr] = useState<string | null>(null);
  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer pj-drawer">
        <h2>{parentId ? 'Add a sub-task' : 'Add a task'}</h2>
        {err && <p className="block-note">{err}</p>}
        <label className="cf-field"><span>Name</span>
          <input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
        <label className="lv-check"><input type="checkbox" checked={f.isMilestone}
          onChange={(e) => setF({ ...f, isMilestone: e.target.checked })} />
          <span>Milestone (a point in time, drawn as a diamond)</span></label>
        <div className="pj-two">
          <label className="cf-field"><span>{f.isMilestone ? 'Date' : 'Start'}</span>
            <input className="input" type="date" value={f.startsOn}
                   onChange={(e) => setF({ ...f, startsOn: e.target.value })} /></label>
          {!f.isMilestone && (
            <label className="cf-field"><span>End</span>
              <input className="input" type="date" value={f.endsOn}
                     onChange={(e) => setF({ ...f, endsOn: e.target.value })} /></label>
          )}
        </div>
        <label className="cf-field"><span>Assigned to</span>
          <select className="input sel" value={f.assigneeId}
                  onChange={(e) => setF({ ...f, assigneeId: e.target.value })}>
            <option value="">Unassigned</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
          </select></label>
        <div className="exp-decide-actions">
          <button className="btn primary" onClick={async () => {
            try {
              if (!f.name.trim()) throw new Error('A name first.');
              if (!f.isMilestone && f.endsOn < f.startsOn) throw new Error('It cannot end before it starts.');
              await createTask({ projectId, parentId, name: f.name.trim(),
                startsOn: f.startsOn, endsOn: f.endsOn, isMilestone: f.isMilestone,
                assigneeId: f.assigneeId || undefined, sortOrder: siblingMax });
              onDone();
            } catch (e) { setErr(String((e as Error).message)); }
          }}>Add</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}

function TaskDrawer({ task, tasks, deps, members, onClose, onChanged, onMsg }: {
  task: Task; tasks: Task[]; deps: Dep[]; members: Member[];
  onClose: () => void; onChanged: () => void; onMsg: (s: string) => void;
}) {
  const [f, setF] = useState({ name: task.name, startsOn: task.starts_on, endsOn: task.ends_on,
    percent: task.percent_complete, assigneeId: task.assignee_id ?? '', notes: task.notes ?? '' });
  const [newDep, setNewDep] = useState('');
  const [newLag, setNewLag] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const myDeps = deps.filter((d) => d.task_id === task.id);
  const taskName = (id: string) => tasks.find((t) => t.id === id)?.name ?? '';
  const candidates = tasks.filter((t) => t.id !== task.id
    && !myDeps.some((d) => d.depends_on_task_id === t.id)
    && !tasks.some((x) => x.parent_id === t.id));
  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer pj-drawer">
        <h2>{task.is_milestone ? 'Milestone' : 'Task'}</h2>
        {err && <p className="block-note">{err}</p>}
        <label className="cf-field"><span>Name</span>
          <input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
        <div className="pj-two">
          <label className="cf-field"><span>{task.is_milestone ? 'Date' : 'Start'}</span>
            <input className="input" type="date" value={f.startsOn}
                   onChange={(e) => setF({ ...f, startsOn: e.target.value })} /></label>
          {!task.is_milestone && (
            <label className="cf-field"><span>End</span>
              <input className="input" type="date" value={f.endsOn}
                     onChange={(e) => setF({ ...f, endsOn: e.target.value })} /></label>
          )}
        </div>
        <div className="pj-two">
          <label className="cf-field"><span>Assigned to</span>
            <select className="input sel" value={f.assigneeId}
                    onChange={(e) => setF({ ...f, assigneeId: e.target.value })}>
              <option value="">Unassigned</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
            </select></label>
          <label className="cf-field"><span>Complete: {f.percent}%</span>
            <input className="input" type="range" min={0} max={100} step={5} value={f.percent}
                   onChange={(e) => setF({ ...f, percent: Number(e.target.value) })} /></label>
        </div>
        <label className="cf-field"><span>Notes</span>
          <input className="input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></label>

        <h3 className="pj-h3">Comes after</h3>
        {myDeps.length === 0 && <p className="muted">No predecessors; it can start any time.</p>}
        {myDeps.map((d) => (
          <div className="pj-deprow" key={d.id}>
            <span>{taskName(d.depends_on_task_id)}{d.lag_days > 0 && <> + {d.lag_days}d lag</>}</span>
            <button className="link-btn" onClick={() =>
              removeDep(d.id).then(onChanged).catch((x) => setErr(x.message))}>unlink</button>
          </div>
        ))}
        <div className="pj-depadd">
          <select className="input sel" value={newDep} onChange={(e) => setNewDep(e.target.value)}>
            <option value="">Add a predecessor…</option>
            {candidates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <input className="input pj-lag" type="number" min={0} title="Lag days"
                 value={newLag} onChange={(e) => setNewLag(Number(e.target.value))} />
          <button className="btn" disabled={!newDep} onClick={() =>
            addDep(task.id, newDep, newLag).then(() => { setNewDep(''); setNewLag(0); onChanged(); })
              .catch((x) => setErr(x.message))}>Link</button>
        </div>

        <div className="exp-decide-actions">
          <button className="btn primary" onClick={async () => {
            try {
              await updateTask(task.id, { name: f.name.trim(), starts_on: f.startsOn,
                ends_on: task.is_milestone ? f.startsOn : f.endsOn,
                percent_complete: f.percent, assignee_id: f.assigneeId || null,
                notes: f.notes || null } as Partial<Task>);
              onChanged();
            } catch (e) { setErr(String((e as Error).message)); }
          }}>Save</button>
          <button className="btn" onClick={() => {
            if (!window.confirm('Delete this task (and its sub-tasks and links)?')) return;
            deleteTask(task.id).then(() => { onMsg('Deleted.'); onChanged(); })
              .catch((x) => setErr(x.message));
          }}>Delete</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}
